/**
 * A module's results, and — once enough of the class has finished — its top three.
 *
 * One card, two faces, and the rule that chooses between them is a single count:
 * below a hundred finishers the card is a **results list** (everyone, ranked, with
 * the learners who stopped early listed under them), and at a hundred it is a
 * **leaderboard** (the same ranking, cut to a podium). It is one ranking drawn
 * twice rather than two features, which is why the podium is always computed and
 * simply revealed — a board that changed its *rule* at a hundred would be two
 * different competitions wearing one name.
 *
 * Four decisions shape it:
 *
 *  - **It leads with the ranking rule, not with a score.** Every row prints the
 *    points, the time *and* the ratio, because the order is points per minute and
 *    a reader who cannot see both numbers would read a lower score above a higher
 *    one as a bug. Saying the rule out loud — in the header and on every row — is
 *    what makes the order legible.
 *  - **The gate is visible, and it is good news.** Under a hundred the card shows
 *    *how many finishers there are* and what the wait is for. A count that appears
 *    without explanation reads as a locked feature; a progress line reads as a
 *    class filling up.
 *  - **The unfinished are listed, and never ranked.** They are under a rule of
 *    their own with how far they got, because the alternative — ranking somebody
 *    on four capsules against people who did six — is a lie about the competition,
 *    and leaving them out is a lie about the class.
 *  - **Your own row is marked.** A board is a class list; a learner who has played
 *    should be able to find themselves without reading a hundred names, so the row
 *    is tinted and its label says so.
 *
 * It is drawn in the app's own chrome rather than in the palette of the work being
 * played (which is what a book's class report does): a book is a *book*, and a
 * module is the app's own object — a list of capsules — so its leaderboard should
 * look like the app's list, not like somebody's paper.
 */
import {StyleSheet, View} from 'react-native'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {
  formatRate,
  formatTime,
  type ModuleLeaderboard,
  type ModulePlayerScore,
} from '#/lib/moduleLeaderboard'
import {sanitizeHandle} from '#/lib/strings/handles'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {UserAvatar} from '#/view/com/util/UserAvatar'

/** How many rows the list shows once the board is a leaderboard. */
const BOARD_ROWS = 20

export function ModuleLeaderboardCard({
  title,
  board,
  currentDid,
  loading,
  failed,
}: {
  /** The module's own name, printed above the board. */
  title: string
  board: ModuleLeaderboard
  /** Whose row to mark, when they are on the board. */
  currentDid?: string
  loading?: boolean
  failed?: boolean
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const behind = Math.max(0, board.threshold - board.finishers)
  const rows =
    board.mode === 'leaderboard' ? board.ranked.slice(0, BOARD_ROWS) : board.all

  return (
    <View
      testID="module-leaderboard-card"
      style={[
        styles.card,
        t.atoms.border_contrast_low,
        t.atoms.bg_contrast_25,
      ]}>
      <View style={[a.px_md, a.pt_md, a.gap_xs]}>
        <Text
          numberOfLines={1}
          style={[a.text_xs, a.font_semi_bold, t.atoms.text_contrast_medium]}>
          {title}
        </Text>
        <Text
          testID="module-leaderboard-title"
          style={[a.text_xl, a.font_bold, t.atoms.text]}>
          {board.mode === 'leaderboard' ? (
            <Trans>Leaderboard</Trans>
          ) : (
            <Trans>Results</Trans>
          )}
        </Text>
        <Text
          testID="module-leaderboard-gate"
          style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {loading ? (
            <Trans>Gathering the class's plays…</Trans>
          ) : failed ? (
            <Trans>
              The class's plays could not be loaded. Try again in a moment.
            </Trans>
          ) : board.empty ? (
            <Trans>
              No one has finished this module yet. Results appear here as
              learners play it.
            </Trans>
          ) : board.mode === 'leaderboard' ? (
            <Plural
              value={board.finishers}
              one="One learner has finished this module."
              other="# learners have finished this module."
            />
          ) : (
            <Trans>
              {board.finishers} of {board.threshold} finishers — the top 3
              unlock at {board.threshold}.
            </Trans>
          )}
        </Text>
        {!loading && !failed && !board.empty && board.mode === 'results' ? (
          <View
            testID="module-leaderboard-progress"
            style={[styles.track, t.atoms.bg_contrast_50]}>
            <View
              style={[
                styles.fill,
                {
                  backgroundColor: t.palette.primary_500,
                  width: `${Math.min(
                    100,
                    Math.round((board.finishers / board.threshold) * 100),
                  )}%`,
                },
              ]}
            />
          </View>
        ) : null}
        {!loading && !failed && board.mode === 'results' && behind > 0 ? (
          <Text
            testID="module-leaderboard-next"
            style={[a.text_xs, t.atoms.text_contrast_medium]}>
            <Plural
              value={behind}
              one="One more finish and the board opens."
              other="# more finishes and the board opens."
            />
          </Text>
        ) : null}
      </View>

      {board.podium.length > 0 && !loading ? (
        <View testID="module-leaderboard-podium" style={[styles.podium]}>
          {/* 2nd · 1st · 3rd, so the winner is in the middle and raised. */}
          {[1, 0, 2].map(place => {
            const player = board.podium[place]
            if (!player) return <View key={place} style={[a.flex_1]} />
            return (
              <PodiumColumn
                key={player.did}
                player={player}
                isCurrent={player.did === currentDid}
                rank={(place + 1) as 1 | 2 | 3}
              />
            )
          })}
        </View>
      ) : null}

      {rows.length > 0 && !loading && !failed ? (
        <View style={[a.px_md, a.pb_md, a.gap_xs]}>
          {rows.map(player => (
            <PlayerRow
              key={player.did}
              player={player}
              isCurrent={player.did === currentDid}
            />
          ))}
        </View>
      ) : null}

      {board.unfinished.length > 0 && !loading && !failed ? (
        <View testID="module-leaderboard-unfinished" style={[a.px_md, a.pb_md]}>
          <Text
            style={[
              a.text_xs,
              a.font_semi_bold,
              a.pb_xs,
              t.atoms.text_contrast_medium,
            ]}>
            <Trans>Didn't finish</Trans>
          </Text>
          <View style={[a.gap_xs]}>
            {board.unfinished.map(player => (
              <PlayerRow
                key={player.did}
                player={player}
                isCurrent={player.did === currentDid}
              />
            ))}
          </View>
          <Text style={[a.text_xs, a.pt_sm, t.atoms.text_contrast_medium]}>
            <Trans>
              Only a learner who played every capsule is ranked — a part-way run
              cannot be compared with a finished one.
            </Trans>
          </Text>
        </View>
      ) : null}
    </View>
  )
}

/** One of the three faces at the top of the board. */
function PodiumColumn({
  player,
  rank,
  isCurrent,
}: {
  player: ModulePlayerScore
  rank: 1 | 2 | 3
  isCurrent: boolean
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const medal = rank === 1 ? '#d4af37' : rank === 2 ? '#a8b3bd' : '#b07d4a'
  const height = rank === 1 ? 46 : 34

  return (
    <View
      testID={`module-leaderboard-podium-${rank}`}
      style={[a.flex_1, a.align_center, a.gap_xs]}>
      <View
        style={[
          a.relative,
          a.rounded_full,
          {
            width: rank === 1 ? 52 : 44,
            height: rank === 1 ? 52 : 44,
            borderWidth: 2,
            borderColor: isCurrent ? t.palette.primary_500 : medal,
          },
        ]}>
        <UserAvatar
          type="user"
          size={rank === 1 ? 48 : 40}
          avatar={player.avatar}
        />
      </View>
      <Text numberOfLines={1} style={[a.text_xs, a.font_bold, t.atoms.text]}>
        {player.name ?? sanitizeHandle(player.handle ?? player.did, '@')}
      </Text>
      <Text
        testID={`module-leaderboard-podium-rate-${rank}`}
        style={[a.text_xs, t.atoms.text_contrast_medium]}>
        {l`${formatRate(player.pointsPerMinute)} pt/min`}
      </Text>
      <View
        style={[
          styles.podiumStep,
          {
            height,
            backgroundColor: t.atoms.bg_contrast_50.backgroundColor,
          },
        ]}>
        <Text style={[a.text_xs, a.font_bold, {color: medal}]}>
          {l`${player.points} pts`}
        </Text>
      </View>
    </View>
  )
}

/**
 * One learner's row.
 *
 * The three numbers are on it in the order they matter: the rank, the ratio that
 * earned it, and then the score and the time it came from. A row that printed the
 * score first would put the *reason* for the order in the least prominent place.
 */
function PlayerRow({
  player,
  isCurrent,
}: {
  player: ModulePlayerScore
  isCurrent: boolean
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View
      testID={`module-leaderboard-row-${player.did}`}
      style={[
        styles.row,
        isCurrent
          ? {backgroundColor: t.atoms.bg_contrast_50.backgroundColor}
          : null,
      ]}>
      <Text
        testID={`module-leaderboard-rank-${player.did}`}
        style={[
          styles.rank,
          a.font_bold,
          player.rank == null ? t.atoms.text_contrast_medium : t.atoms.text,
        ]}>
        {player.rank ?? '–'}
      </Text>
      <UserAvatar type="user" size={30} avatar={player.avatar} />
      <View style={[a.flex_1]}>
        <Text
          numberOfLines={1}
          style={[a.text_sm, a.font_semi_bold, t.atoms.text]}>
          {player.name ?? sanitizeHandle(player.handle ?? player.did, '@')}
          {isCurrent ? (
            <Text style={[a.text_xs]}>
              {' '}
              · <Trans>you</Trans>
            </Text>
          ) : null}
        </Text>
        <Text
          numberOfLines={1}
          style={[a.text_xs, t.atoms.text_contrast_medium]}>
          {player.handle ? `${sanitizeHandle(player.handle, '@')} · ` : ''}
          {player.capsulesDone < player.capsuleCount
            ? l`${player.capsulesDone} of ${player.capsuleCount} capsules`
            : l`all ${player.capsuleCount} capsules`}
          {player.plays > player.capsuleCount
            ? ` · ${l`${player.plays} plays`}`
            : ''}
        </Text>
      </View>
      <View style={[a.align_end]}>
        <Text
          testID={`module-leaderboard-rate-${player.did}`}
          style={[a.text_sm, a.font_bold, {color: t.palette.primary_500}]}>
          {l`${formatRate(player.pointsPerMinute)} pt/min`}
        </Text>
        <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
          {l`${player.points} pts`} · {formatTime(player.timeMs)}
        </Text>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 14,
    overflow: 'hidden',
  },
  track: {
    height: 5,
    borderRadius: 3,
    overflow: 'hidden',
    marginTop: 6,
  },
  fill: {height: '100%'},
  podium: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 18,
    paddingBottom: 6,
  },
  podiumStep: {
    width: '100%',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  rank: {
    width: 22,
    textAlign: 'center',
    fontVariant: ['tabular-nums'],
  },
})
